import {ApiClient} from '@hconnect/apiclient'
import axios, {AxiosError} from 'axios'
import {useCallback, useEffect, useState} from 'react'
import {useTranslation} from 'react-i18next'

import {ProcessStage} from '../types'

interface UseProcessStagesOptions<T> {
  enabled?: boolean
  select?: (data: Map<string, string>) => T
  onSuccess?: (data: T) => void
  onError?: (error: AxiosError) => void
}

export const useProcessStages = <T = Map<string, string>>(
  plantId: string,
  options?: UseProcessStagesOptions<T>
) => {
  const {
    i18n: {language}
  } = useTranslation()
  const {enabled = true, select, onSuccess, onError} = options || {}

  const [data, setData] = useState<T | undefined>(undefined)
  const [error, setError] = useState<AxiosError | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  const handleFetchProcessStages = useCallback(async () => {
    if (!plantId) return

    setIsLoading(true)

    try {
      const path = `/shifthandover/${plantId}/process-stages`
      const params = {params: {language}}

      const response = await ApiClient.axiosInstance.get<ProcessStage[]>(path, params)

      const stagesMap = new Map(
        response.data?.map((stage: ProcessStage) => [stage.code, stage.name])
      )

      setData(() => {
        const transformedData = select ? select(stagesMap) : (stagesMap as T)

        onSuccess?.(transformedData)
        setError(null)
        return transformedData
      })
    } catch (error) {
      if (axios.isAxiosError(error)) {
        setError(error)
        onError?.(error)
      } else {
        console.error('Unexpected error:', error)
      }
    } finally {
      setIsLoading(false)
    }
  }, [plantId, language, select, onSuccess, onError])

  useEffect(() => {
    if (plantId && enabled) {
      void handleFetchProcessStages()
    }
  }, [enabled, handleFetchProcessStages, plantId])

  return {
    data: data as T,
    error,
    isLoading,
    refetch: handleFetchProcessStages
  }
}
